// The document REGISTER: a record of each document, not the file. The files stay in Finder on the Mac
// (SERB Express / Shipments / SRB-711 / …). Made-up examples for the design (see lib/sample.ts).
import { sampleOnly } from '../../lib/sample';
import type { Tone } from '../../components/tone';

import type { DocRecord, DocStatus } from '@serb/shared';

export type { DocRecord, DocStatus };
export const DOC_STATUS_TONE: Record<DocStatus, Tone> = {
  Received: 'success',
  Missing: 'error',
  'Needs Review': 'warning',
  Verified: 'success',
  Draft: 'neutral',
};
export const DOC_STATUSES = Object.keys(DOC_STATUS_TONE) as DocStatus[];

export const DOC_TYPES = [
  'Commercial Invoice',
  'Packing List',
  'Cargo Specification',
  'Customer Quotation',
  'Rate Offer',
  'Bill of Lading',
  'Air Waybill',
  'Booking Confirmation',
  'Agent Invoice',
  'Insurance',
  'Other',
];
export const DOC_SOURCES = ['Customer', 'SERB', 'Overseas Agent', 'Carrier', 'Accountant'];

/** "Commercial Invoice.pdf" */
export const fileName = (r: Pick<DocRecord, 'name' | 'ext'>) => (r.ext ? `${r.name}.${r.ext}` : r.name);

/** The folder layout on the Mac: SERB Express / Shipments / <SRB> / <file> */
export const finderPath = (srb: string, r: Pick<DocRecord, 'name' | 'ext'>) =>
  srb ? `SERB Express/Shipments/${srb}/${fileName(r)}` : '';

const rec = (
  id: string,
  name: string,
  ext: string,
  srb: string,
  type: string,
  source: string,
  status: DocStatus,
  date: string,
  notes = '',
): DocRecord => ({
  id,
  name,
  ext,
  srb,
  type,
  source,
  status,
  date,
  notes,
  path: status === 'Missing' ? '' : finderPath(srb, { name, ext }),
});

export const INITIAL_DOCS: DocRecord[] = sampleOnly([
  rec('d1', 'Commercial Invoice', 'pdf', 'SRB-711', 'Commercial Invoice', 'Customer', 'Received', '2025-07-23'),
  rec('d2', 'Packing List', 'pdf', 'SRB-711', 'Packing List', 'Customer', 'Received', '2025-07-23'),
  rec('d3', 'Cargo Specs', 'xlsx', 'SRB-711', 'Cargo Specification', 'Customer', 'Verified', '2025-07-22'),
  rec(
    'd4',
    'Quotation',
    'docx',
    'SRB-640',
    'Customer Quotation',
    'SERB',
    'Draft',
    '2025-07-20',
    'Draft made from the SERB Word template. Not sent yet.',
  ),
  rec(
    'd5',
    'Agent Rate Offer',
    'pdf',
    'SRB-539',
    'Rate Offer',
    'Overseas Agent',
    'Needs Review',
    '2025-07-19',
    'Check the rate against the last offer.',
  ),
  rec(
    'd6',
    'BL Draft',
    'pdf',
    'SRB-596',
    'Bill of Lading',
    'Carrier',
    'Needs Review',
    '2025-07-18',
    'The customer has to check the draft BL.',
  ),
  rec('d7', 'Agent Invoice', 'pdf', 'SRB-518', 'Agent Invoice', 'Overseas Agent', 'Received', '2025-07-17'),
  rec(
    'd8',
    'Insurance Certificate',
    'docx',
    'SRB-640',
    'Insurance',
    'Customer',
    'Missing',
    '2025-07-12',
    'Ask the customer for the certificate.',
  ),
  rec('d9', 'AWB_EK045', 'pdf', 'SRB-640', 'Air Waybill', 'Carrier', 'Received', '2025-07-27'),
  rec('d10', 'Booking Confirmation', 'pdf', 'SRB-640', 'Booking Confirmation', 'Carrier', 'Verified', '2025-07-22'),
  rec('d11', 'Commercial Invoice', 'pdf', 'SRB-640', 'Commercial Invoice', 'Customer', 'Received', '2025-07-25'),
  rec('d12', 'Packing List', 'pdf', 'SRB-640', 'Packing List', 'Customer', 'Received', '2025-07-25'),
  rec(
    'd13',
    'Scan_0412',
    'pdf',
    '',
    'Other',
    'Customer',
    'Needs Review',
    '2025-07-21',
    'Not yet linked to a shipment.',
  ),
  rec('d14', 'Bank transfer slip', 'jpg', '', 'Other', 'Accountant', 'Received', '2025-07-20'),
  rec('d15', 'Untitled', 'docx', '', 'Other', 'SERB', 'Draft', '2025-07-15'),
]);
